"use client"

import { useState, useEffect } from "react"

/**
 * Hook to detect if a media query matches
 * @param query - CSS media query string (e.g., '(max-width: 768px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(false)

    useEffect(() => {
        const media = window.matchMedia(query)
        setMatches(media.matches)

        const listener = (e: MediaQueryListEvent) => setMatches(e.matches)
        media.addEventListener("change", listener)
        return () => media.removeEventListener("change", listener)
    }, [query])

    return matches
}

/**
 * Hook to detect if the viewport is mobile-sized
 * @returns boolean - true if viewport width is 768px or less
 */
export function useIsMobile(): boolean {
    return useMediaQuery("(max-width: 768px)")
}

/**
 * Hook to detect if the viewport is tablet-sized or smaller
 * @returns boolean - true if viewport width is 1024px or less
 */
export function useIsTablet(): boolean {
    return useMediaQuery("(max-width: 1024px)")
}

/**
 * Hydration-safe replacement for framer-motion's useReducedMotion: that hook reads
 * matchMedia during the first client render (null on the server), so visitors with
 * reduced motion get a markup mismatch. This one is false until after mount.
 */
export function usePrefersReducedMotion(): boolean {
    return useMediaQuery("(prefers-reduced-motion: reduce)")
}
